
import { Component, computed, effect, inject, input, model, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AuthenticationService } from '@my-aon/auth';
import { VendorsModule } from '@my-aon/vendors';
import { SharedModule } from '@my-aon/shared';
import { PermissionViewModel, RolesService } from '../../../lib';


@Component({
    selector: 'app-role-permissions-list',
    templateUrl: './role-permissions-list.component.html',
    styleUrl: './role-permissions-list.component.scss',
    standalone: true,
    imports: [
        VendorsModule,
        SharedModule
    ],
})
export class RolePermissionsListComponent implements OnInit {

    readonly auth = inject(AuthenticationService);
    readonly translateService = inject(TranslateService);
    readonly rolesService = inject(RolesService);

    $readOnly = input.required<boolean>({ alias: 'readOnly' });
    $permissions = model.required<PermissionViewModel[]>({ alias: 'permissions' });
    $userRoles = input<string[]>([], { alias: 'userRoles' });

    allowText = this.translateService.instant("PERMISSIONS.LABEL_ALLOW")
    denyText = this.translateService.instant("PERMISSIONS.LABEL_DENY")

    switchDataSource = [{ text: this.allowText, value: true }, { text: this.denyText, value: false }];

    tabs: Array<{ id: string, text: string, icon?: string }> = ['general', 'sys', 'hw', 'auto_fleet'].map(id => ({
        id: id,
        text: this.translateService.instant("PERMISSIONS.PERMISSION." + id)
    }));

    $selectedTabIndex = signal<number>(0);

    $permissionsGrouped = computed(() => {

        return this.tabs.map(tab => {
            return {
                ...tab,
                permissions: this.$permissions().filter(p => p.prefix == tab.id)
            };
        });
    });

    constructor() {
        effect(() => {
            const perms = this.$permissions();
            if (perms.length)
                this.init();
        });
    }

    ngOnInit(): void {
        if (this.$permissions()?.length)
            this.init();
    }

    private async init() {
        const allRoles = this.rolesService.$roles();
        const userRoles = this.$userRoles().map(x => allRoles.find(y => y.Name == x)).filter(x => !!x);

        const permissionsMap = new Map(this.$permissions().map(x => [x.key, x]));
        userRoles.forEach(r => {
            r.PermissionKey.forEach(p => {
                const affectingRoles = permissionsMap.get(p.Key)?.affectingRoles;
                if (affectingRoles && !affectingRoles.includes(r.Name))
                    affectingRoles.push(r.Name);
            })
        });
    }

    canChangePermission(key: string) {
        if (this.$readOnly()) return false;
        // For now, prevent only SYS permissions.
        return !key.startsWith('sys') || this.auth.hasAnyPermission(key);
    }
}
